import React, { useEffect, useRef, useState } from "react";
import * as signalR from "@microsoft/signalr";
import api from "../../api";
import ConversationList from "./components/ConversationList";
import ConversationPanel from "./components/ConversationPanel";
import { formatDateTime } from "./utils/chatHelpers";

const HUB_URL = `${process.env.REACT_APP_API_URL.replace(/\/api\/?$/, "")}/hubs/support`;

const ChatIndex = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [error, setError] = useState("");

  const messagesEndRef = useRef(null);
  const previousMessageCountRef = useRef(0);
  const connectionRef = useRef(null);
  const joinedConversationRef = useRef(null);
  const selectedConversationIdRef = useRef(null);

  useEffect(() => {
    selectedConversationIdRef.current = selectedConversation?.id || null;
  }, [selectedConversation?.id]);

  // ---------- Helpers ----------

  const sortByActivity = (items) =>
    [...items].sort((a, b) =>
      new Date(b.lastMessageAt || 0) - new Date(a.lastMessageAt || 0)
    );

  const upsertConversationMeta = (conversationId, payload) => {
    setConversations((prev) =>
      sortByActivity(
        prev.map((conv) =>
          conv.id === conversationId ? { ...conv, ...payload } : conv
        )
      )
    );
  };

  const appendMessageToSelected = (newMessage) => {
    setSelectedConversation((prev) => {
      if (!prev) return prev;
      const exists = (prev.messages || []).some((m) => m.id === newMessage.id);
      return {
        ...prev,
        status: "open",
        lastMessageAt: newMessage.createdAt ?? prev.lastMessageAt,
        messages: exists ? prev.messages : [...(prev.messages || []), newMessage],
      };
    });
  };

  // ---------- API ----------

  const fetchConversationDetails = async (conversationId, silent = false) => {
    try {
      if (!silent) setError("");
      const res = await api.get(`/admin/support/conversations/${conversationId}`);
      const conv = res.data.conversation;

      setSelectedConversation(conv);
      upsertConversationMeta(conversationId, {
        unreadCount: 0,
        status: conv.status,
        lastMessageAt: conv.lastMessageAt,
      });

      return conv;
    } catch (err) {
      if (!silent) setError("Erreur lors du chargement de la conversation.");
      return null;
    }
  };

  const fetchConversations = async (showLoader = false) => {
    try {
      if (showLoader) setLoading(true);
      setError("");

      const res = await api.get("/admin/support/conversations/open");
      const items = sortByActivity(res.data.conversations || []);
      setConversations(items);

      if (items.length === 0) { setSelectedConversation(null); return; }

      if (!selectedConversationIdRef.current) {
        await fetchConversationDetails(items[0].id, true);
      }
    } catch (err) {
      setError("Erreur lors du chargement des conversations.");
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  useEffect(() => { fetchConversations(true); }, []);

  // ---------- SignalR ----------

  useEffect(() => {
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(HUB_URL, { withCredentials: true })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    connection.on("ConversationUpdated", (incomingConv) => {
      const isSelected = selectedConversationIdRef.current === incomingConv.id;

      setConversations((prev) => {
        const exists = prev.some((c) => c.id === incomingConv.id);
        const updated = isSelected
          ? { ...incomingConv, unreadCount: 0 }
          : incomingConv;

        const next = exists
          ? prev.map((c) => c.id === updated.id ? { ...c, ...updated } : c)
          : [updated, ...prev];

        return sortByActivity(next);
      });

      setSelectedConversation((prev) => {
        if (!prev || prev.id !== incomingConv.id) return prev;
        return { ...prev, status: incomingConv.status, lastMessageAt: incomingConv.lastMessageAt };
      });
    });

    connection.on("MessageReceived", (newMessage) => {
      const isSelected = selectedConversationIdRef.current === newMessage.conversationId;

      if (isSelected) {
        const msg = newMessage.senderRole === "user"
          ? { ...newMessage, isRead: true }
          : newMessage;

        appendMessageToSelected(msg);

        setConversations((prev) =>
          sortByActivity(prev.map((c) =>
            c.id === newMessage.conversationId
              ? { ...c, status: "open", lastMessageAt: newMessage.createdAt, unreadCount: 0 }
              : c
          ))
        );
      }
    });

    connection.start().catch((err) => console.error("SignalR error:", err));
    connectionRef.current = connection;

    return () => {
      connection.stop();
      connectionRef.current = null;
      joinedConversationRef.current = null;
    };
  }, []);

  // Rejoindre le groupe conversation quand sélection change
  useEffect(() => {
    const connection = connectionRef.current;
    if (!connection || !selectedConversation?.id) return;
    if (joinedConversationRef.current === selectedConversation.id) return;

    const join = async () => {
      try {
        if (connection.state !== signalR.HubConnectionState.Connected) {
          await new Promise((resolve) => {
            const interval = setInterval(() => {
              if (connection.state === signalR.HubConnectionState.Connected) {
                clearInterval(interval);
                resolve();
              }
            }, 100);
          });
        }
        if (joinedConversationRef.current) {
          await connection.invoke("LeaveConversation", joinedConversationRef.current);
        }
        await connection.invoke("JoinConversation", selectedConversation.id);
        joinedConversationRef.current = selectedConversation.id;
      } catch (err) {
        console.error("JoinConversation error:", err);
      }
    };

    join();
  }, [selectedConversation?.id]);

  // Scroll auto
  useEffect(() => {
    const count = selectedConversation?.messages?.length || 0;
    if (count > previousMessageCountRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }
    previousMessageCountRef.current = count;
  }, [selectedConversation?.messages]);

  // ---------- Actions ----------

  const handleSelectConversation = async (conversation) => {
    setReply("");
    await fetchConversationDetails(conversation.id);
  };

  const handleSendReply = async () => {
    if (!reply.trim() || !selectedConversation || selectedConversation.status === "closed") return;

    try {
      setSending(true);
      setError("");

      const res = await api.post(
        `/admin/support/conversations/${selectedConversation.id}/messages`,
        { message: reply.trim() }
      );

      const newMessage = res.data.message;
      appendMessageToSelected(newMessage);
      upsertConversationMeta(selectedConversation.id, {
        status: "open",
        unreadCount: 0,
        lastMessageAt: newMessage.createdAt,
      });
      setReply("");
    } catch (err) {
      setError("Erreur lors de l'envoi de la réponse.");
    } finally {
      setSending(false);
    }
  };

  const handleUpdateStatus = async (status) => {
    if (!selectedConversation) return;

    try {
      setUpdatingStatus(true);
      setError("");

      const res = await api.patch(
        `/admin/support/conversations/${selectedConversation.id}/status`,
        { status }
      );

      const updated = res.data.conversation;
      setSelectedConversation((prev) => prev ? { ...prev, status: updated.status } : prev);
      upsertConversationMeta(selectedConversation.id, { status: updated.status });
    } catch (err) {
      setError("Erreur lors de la mise à jour du statut.");
    } finally {
      setUpdatingStatus(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100">
        <main className="mx-auto max-w-7xl px-6 py-6">
          <div className="rounded-3xl border border-slate-800 bg-slate-900 p-6">
            <p className="text-slate-300">Chargement des conversations...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <main className="mx-auto max-w-7xl px-6 py-6">
        <section className="mb-6 overflow-hidden rounded-3xl border border-slate-800 bg-gradient-to-r from-slate-900 via-slate-800 to-cyan-900 shadow-sm">
          <div className="px-8 py-8 md:px-10 md:py-10">
            <p className="mb-3 inline-flex rounded-full bg-white/10 px-3 py-1 text-sm font-medium text-cyan-100">
              Dashboard Support
            </p>
            <h1 className="mb-3 text-3xl font-bold text-white md:text-4xl">Centre de support</h1>
            <p className="max-w-3xl text-sm leading-7 text-slate-200 md:text-base">
              Gérez les demandes utilisateurs, suivez les échanges en temps réel et répondez depuis une interface claire et centralisée.
            </p>
          </div>
        </section>

        {error && (
          <div className="mb-6 rounded-2xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {error}
          </div>
        )}

        <section className="grid grid-cols-1 gap-6 xl:grid-cols-12">
          <ConversationList
            conversations={conversations}
            selectedConversation={selectedConversation}
            onSelectConversation={handleSelectConversation}
            formatDateTime={formatDateTime}
          />
          <ConversationPanel
            selectedConversation={selectedConversation}
            reply={reply}
            setReply={setReply}
            sending={sending}
            updatingStatus={updatingStatus}
            handleSendReply={handleSendReply}
            handleUpdateStatus={handleUpdateStatus}
            messagesEndRef={messagesEndRef}
            formatDateTime={formatDateTime}
          />
        </section>
      </main>
    </div>
  );
};

export default ChatIndex;